import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// Customers admin: invite + revoke + list active customers.

export default function Customers() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [customers, setCustomers] = useState(null);
  const [invitations, setInvitations] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const unsubCustomers = onSnapshot(
      query(collection(db, 'vendors', vendorId, 'users'), where('role', '==', 'customer')),
      (snap) => setCustomers(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => !u.deletedAt),
      ),
      (err) => setError(err.message),
    );
    const unsubInv = onSnapshot(
      collection(db, 'vendors', vendorId, 'invitations'),
      (snap) => setInvitations(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((i) => i.status === 'pending')
          .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)),
      ),
      (err) => setError(err.message),
    );
    return () => { unsubCustomers(); unsubInv(); };
  }, [vendorId]);

  async function revoke(inviteId) {
    if (!confirm('Revoke this invitation? The link will stop working.')) return;
    try {
      await httpsCallable(functions, 'revokeInvitation')({ inviteId });
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Requesters</h2>
        <button onClick={() => setShowInvite(true)} className="btn-primary">+ Invite requester</button>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: -8 }}>
        Each requester gets an invitation link they use to set up their account and start submitting requests.
      </p>

      {error && <div className="error">{error}</div>}

      <h3 style={{ marginTop: 24 }}>Pending invitations</h3>
      {invitations === null && <div className="placeholder">Loading…</div>}
      {invitations && invitations.length === 0 && (
        <div className="placeholder">No pending invitations.</div>
      )}
      {invitations && invitations.length > 0 && (
        <table className="data-table">
          <thead>
            <tr><th>Email</th><th>Site</th><th>Expires</th><th></th></tr>
          </thead>
          <tbody>
            {invitations.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>{inv.siteName || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td>{inv.expiresAt?.toDate?.().toLocaleDateString() || ''}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => revoke(inv.id)} className="btn-ghost-danger">Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={{ marginTop: 32 }}>Active requesters</h3>
      {customers === null && <div className="placeholder">Loading…</div>}
      {customers && customers.length === 0 && (
        <div className="placeholder">No active requesters yet. Invite your first one above.</div>
      )}
      {customers && customers.length > 0 && (
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Site</th><th>Joined</th></tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id}>
                <td>{c.fullName}</td>
                <td>{c.email}</td>
                <td>{c.siteName || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td>{c.createdAt?.toDate?.().toLocaleDateString() || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showInvite && (
        <InviteCustomerModal
          onClose={() => setShowInvite(false)}
          onInvited={(result) => { setShowInvite(false); setInviteResult(result); }}
        />
      )}
      {inviteResult && (
        <InviteLinkModal info={inviteResult} onClose={() => setInviteResult(null)} />
      )}
    </>
  );
}

function InviteCustomerModal({ onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [siteName, setSiteName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const result = await httpsCallable(functions, 'inviteCustomer')({
        email: email.trim(), siteName: siteName.trim(),
      });
      onInvited({ ...result.data, email: email.trim() });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Invite requester</h3>
        <label>Requester email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label>Site name (optional)</label>
        <input
          type="text" placeholder="e.g. Sydney DC, Melbourne Plant"
          value={siteName} onChange={(e) => setSiteName(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1 }}>
            {busy ? 'Generating…' : 'Generate invite link'}
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}

function InviteLinkModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(info.inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Send this link to {info.email}</h3>
        <p style={{ color: 'var(--muted)' }}>
          Share via email, WhatsApp, or SMS. Link expires in 30 days. The recipient sets their
          own password when they click.
        </p>
        <div className="kv">
          <div><strong>Invite URL</strong></div>
          <div><code style={{ wordBreak: 'break-all' }}>{info.inviteUrl}</code></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={copy} className="btn-ghost" style={{ flex: 1 }}>
            {copied ? 'Copied ✓' : 'Copy link'}
          </button>
          <button onClick={onClose} className="btn-primary" style={{ flex: 1 }}>Done</button>
        </div>
      </div>
    </div>
  );
}
